import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, HelpCircle } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { faqs } from '@shared/faqs';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import { buildBreadcrumbJsonLd } from '../lib/seoSchema';
import { pageSeoMeta } from '@shared/pageSeoMeta';

export default function FaqPage() {
  const { isDark } = useTheme();
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };

  const breadcrumbJsonLd = buildBreadcrumbJsonLd([
    { name: 'Home', url: '/' },
    { name: 'FAQ', url: '/faq' },
  ]);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? 'bg-gray-950' : 'bg-gray-50'}`}>
      <SEO
        {...pageSeoMeta.faq}
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />

      <Breadcrumb items={[{ name: 'Home', href: '/' }, { name: 'FAQ' }]} />

      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="text-center mb-10 sm:mb-14"
        >
          <div className="inline-flex items-center gap-2 mb-3">
            <HelpCircle className="w-4 h-4 text-emerald-500" />
            <span className="text-emerald-500 text-sm font-semibold uppercase tracking-wider">
              Support
            </span>
          </div>
          <h1 className={`text-3xl sm:text-5xl font-extrabold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Frequently Asked <span className="text-gradient">Questions</span>
          </h1>
          <p className={`mt-4 text-base sm:text-lg ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Everything you need to know about nicotine pouches, our products, shipping, and more.
          </p>
        </motion.div>

        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
              className={`rounded-2xl border overflow-hidden transition-all ${
                isDark
                  ? `bg-gray-800 ${openFAQ === i ? 'border-emerald-500/50' : 'border-gray-700'}`
                  : `bg-white ${openFAQ === i ? 'border-emerald-500 shadow-lg shadow-emerald-600/5' : 'border-gray-100'}`
              }`}
            >
              <button
                onClick={() => setOpenFAQ(openFAQ === i ? null : i)}
                className="w-full flex items-center justify-between p-5 text-left"
                aria-expanded={openFAQ === i}
              >
                <span className={`font-semibold pr-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {faq.question}
                </span>
                <motion.span
                  animate={{ rotate: openFAQ === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <ChevronDown size={18} className="text-gray-400" />
                </motion.span>
              </button>
              {/* Always render the answer in the DOM so crawlers see every
                  Q&A. Visually controlled by max-height + opacity rather
                  than conditional mount so SSR + hydration both include
                  the full text. */}
              <motion.div
                initial={false}
                animate={{
                  height: openFAQ === i ? 'auto' : 0,
                  opacity: openFAQ === i ? 1 : 0,
                }}
                transition={{ duration: 0.3 }}
                className="overflow-hidden"
                aria-hidden={openFAQ !== i}
              >
                <p className={`px-5 pb-5 text-sm sm:text-base leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  {faq.answer}
                </p>
              </motion.div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
